import type { ReactNode } from 'react';
import { UMIcon, UMR } from '../../urbanmap/_components';
import { PLAN } from '../proposition-c/tokens';
import { fmt } from '../proposition-c/ui';
import type { ConsumptionImpact } from './hooks';
import type { TeamMember } from './types';

/** Collaborator avatar — 28 px, same density as the UrbanMap reference chrome. */
export function Avatar({ member, size = 28 }: { member: TeamMember; size?: number }) {
  return (
    <span
      title={`${member.name} — ${member.job}`}
      style={{
        width: size, height: size, borderRadius: '50%', flexShrink: 0,
        background: member.owner ? PLAN.accent : UMR.blue, color: UMR.white,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        fontSize: size <= 24 ? 10 : 11, fontWeight: 700, letterSpacing: 0.2, fontFamily: UMR.font,
      }}
    >
      {member.initials}
    </span>
  );
}

export function AvatarStack({ members, max = 4 }: { members: TeamMember[]; max?: number }) {
  const shown = members.slice(0, max);
  const rest = members.length - shown.length;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
      {shown.map((m, i) => (
        <span key={m.id} style={{ marginLeft: i === 0 ? 0 : -8, boxShadow: `0 0 0 2px ${UMR.white}`, borderRadius: '50%' }}>
          <Avatar member={m} size={24} />
        </span>
      ))}
      {rest > 0 && (
        <span style={{ marginLeft: -8, width: 24, height: 24, borderRadius: '50%', background: UMR.grey100, color: UMR.textSecondary, boxShadow: `0 0 0 2px ${UMR.white}`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700 }}>
          +{rest}
        </span>
      )}
    </span>
  );
}

export function Card({ title, icon, accent = PLAN.accent, children }: { title: string; icon: string; accent?: string; children: ReactNode }) {
  return (
    <section style={{ border: `1px solid ${UMR.border}`, borderTop: `4px solid ${accent}`, padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <UMIcon name={icon} size={22} color={accent} />
        <div style={{ fontWeight: 700, fontSize: 16 }}>{title}</div>
      </div>
      {children}
    </section>
  );
}

export function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 14, borderBottom: `1px solid ${UMR.borderSubtle}`, paddingBottom: 4 }}>
      <span style={{ color: UMR.textSecondary }}>{label}</span>
      <strong style={{ textAlign: 'right' }}>{value}</strong>
    </div>
  );
}

/**
 * Variant A of the "impact" UX hypothesis: before / after on the whole perimeter.
 * Variant B is the plain mention below.
 */
export function ImpactCard({ impact, variant, ready }: { impact: ConsumptionImpact | null; variant: 'A' | 'B'; ready: boolean }) {
  if (variant === 'B') {
    return (
      <div style={{ display: 'flex', gap: 12, padding: 12, background: PLAN.accentBg, border: `1px solid ${PLAN.accentBorder}`, fontSize: 14, color: PLAN.accentStrong }}>
        <UMIcon name="calculate" size={20} color={PLAN.accentStrong} />
        <div>
          Les consommations des bâtiments du projet seront <strong>recalculées</strong> avec ces hypothèses.
          Elles différeront de celles affichées dans UrbanMap.
        </div>
      </div>
    );
  }

  const up = (impact?.deltaPct ?? 0) >= 0;
  return (
    <div
      aria-label="Impact des hypothèses sur les consommations"
      style={{ border: `1px solid ${UMR.border}`, padding: 12, display: 'flex', flexDirection: 'column', gap: 8, opacity: ready ? 1 : 0.45 }}
    >
      <div style={{ fontSize: 12, color: UMR.textSecondary, textTransform: 'uppercase', letterSpacing: 0.4 }}>
        Consommation totale du périmètre
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 12, color: UMR.textSecondary }}>UrbanMap</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: UMR.textSecondary }}>{fmt(impact?.umTotal ?? 0)} MWh/an</div>
        </div>
        <UMIcon name="arrow_forward" size={20} color={UMR.textSecondary} />
        <div>
          <div style={{ fontSize: 12, color: PLAN.accentStrong }}>Projet, hypothèses de l’AO</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: PLAN.accentStrong }}>{fmt(impact?.planTotal ?? 0)} MWh/an</div>
        </div>
        <div
          style={{
            marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px',
            background: up ? PLAN.warningBg : PLAN.successBg, color: up ? PLAN.warningText : PLAN.successText, fontSize: 13, fontWeight: 700,
          }}
        >
          <UMIcon name={up ? 'trending_up' : 'trending_down'} size={16} color={up ? PLAN.warningText : PLAN.successText} />
          {up ? '+' : ''}{(impact?.deltaPct ?? 0).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} %
        </div>
      </div>
      <div style={{ fontSize: 12, color: UMR.textSecondary }}>
        Écart normal : il traduit les hypothèses imposées par le cahier des charges, pas une erreur de données.
      </div>
    </div>
  );
}
